import { Api, FullRequestParams, Token, HttpResponse } from './api/Api';
import { debugLog } from 'src/util/logger';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const API_VERSION = process.env.REACT_APP_API_VERSION || 'api/v1';
const AUTH_PUBLIC_PATHS = [
  `/${API_VERSION}/auth/login`,
  `/${API_VERSION}/auth/signup`,
  `/${API_VERSION}/auth/send-verification-email-post-signup`,
  `/${API_VERSION}/auth/verify-email-post-signup`,
  `/${API_VERSION}/request-password-reset`,
  `/${API_VERSION}/verify-password-link`,
  `/${API_VERSION}/reset-password`,
];

// "Remember me": ticked → localStorage (survives closing the browser),
// unticked → sessionStorage (cleared when the browser/tab closes).
// On page load, use whichever storage currently holds a session.
let tokenStore: Storage =
  sessionStorage.getItem(REFRESH_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY)
    ? sessionStorage
    : localStorage;

let accessToken: string | null = tokenStore.getItem(ACCESS_TOKEN_KEY);
let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

// Token-refresh event listeners – used by AppRouter to re-fetch /auth/user
type TokenRefreshListener = () => void;
const tokenRefreshListeners: Set<TokenRefreshListener> = new Set();

export const addTokenRefreshListener = (cb: TokenRefreshListener): void => {
  tokenRefreshListeners.add(cb);
};

export const removeTokenRefreshListener = (cb: TokenRefreshListener): void => {
  tokenRefreshListeners.delete(cb);
};

const notifyTokenRefreshListeners = (): void => {
  tokenRefreshListeners.forEach(cb => cb());
};

// In-memory cache store
const cache = new Map<string, HttpResponse<any, any>>();

const resourceMapping: Record<string, string[]> = {
  projects: [`/${API_VERSION}/projects`],
  sites: [`/${API_VERSION}/sites`, `/${API_VERSION}/projects/`],
  'carbon-analysis': [`/${API_VERSION}/projects/{project_id}/carbon`, `/${API_VERSION}/sites/{site_id}/carbon`],
  'biodiversity-analysis': [`/${API_VERSION}/projects/{project_id}/biodiversity`, `/${API_VERSION}/sites/{site_id}/biodiversity`],
  'environmental-analysis': [`/${API_VERSION}/projects/{project_id}/environmental`, `/${API_VERSION}/sites/{site_id}/environmental`],
  admin: [`/${API_VERSION}/admin`],
  inference: [`/${API_VERSION}/admin/inference`],
  assignments: [`/${API_VERSION}/assignments`],
  users: [`/${API_VERSION}/auth/user`, `/${API_VERSION}/admin/users`],
};

const getResourceFromPath = (path: string): string | null => {
  for (const resource in resourceMapping) {
    if (resourceMapping[resource].some(p => path.startsWith(p))) {
      return resource;
    }
  }
  return null;
};


// Helper to generate a cache key for GET requests
const getCacheKey = (params: FullRequestParams): string | null => {
  if (params.method === 'GET') {
    const resource = getResourceFromPath(params.path);
    const resourceKey = resource ? `resource:${resource}::` : '';
    return `${resourceKey}${params.path}${params.query ? JSON.stringify(params.query) : ''}`;
  }
  return null;
};

const saveAccessToken = (token: string | null) => {
  accessToken = token;
  if (token) {
    tokenStore.setItem(ACCESS_TOKEN_KEY, token);
  } else {
    tokenStore.removeItem(ACCESS_TOKEN_KEY);
  }
};

export const getAccessTokenFromStorage = (): string | null => accessToken;

const clearTokensFromStorage = () => {
  [localStorage, sessionStorage].forEach(store => {
    store.removeItem(ACCESS_TOKEN_KEY);
    store.removeItem(REFRESH_TOKEN_KEY);
  });
  accessToken = null;
  cache.clear(); // Clear cache on logout
  isRefreshing = false; // Reset the refreshing state
  refreshSubscribers = []; // Clear subscribers
};

// Create an instance of the Api class with the base URL for your API
// You can use environment variables to set the base URL
const clientService = new Api({ // Using 'any' for SecurityDataType as it's not strictly typed here
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000', // Fallback URL
  baseApiParams: {
    credentials: 'include',
  },
  securityWorker: async () => {
    const token = getAccessTokenFromStorage();
    if (token) {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return {}; // Return empty object if no token
  },
});

// Store the original request method from the HttpClient instance
const originalRequest = clientService.request.bind(clientService);

const onRefreshed = (token: string | null) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Paths to exclude from caching. (These paths will not be cached)

const CACHE_EXCLUDED_PATHS = [
  `/${API_VERSION}/auth/login`,
  `/${API_VERSION}/auth/signup`,
  `/${API_VERSION}/auth/refresh`,
  `/${API_VERSION}/auth/user`,
  `/${API_VERSION}/sites`, // This exceptional usecase to fetch analysis status of the sites
  `/${API_VERSION}/admin/files`, // Exclude files endpoint from cache to get real-time status updates
  `/${API_VERSION}/admin/projects`,
  `/${API_VERSION}/deployments`, // Deployment device state changes frequently; never cache
  `/${API_VERSION}/device-custody`, // Device custody records must always be fresh
  '/map/markers', // Bounding-box based map markers must never be cached
  '/biodiversity/audio-clips', // Acoustic data might update; always fetch fresh
  `/${API_VERSION}/species-confirmations`, // Species confirmation records must always be fresh
];

clientService.request = async <T = any, E = any>(params: FullRequestParams): Promise<HttpResponse<T, E>> => {
  if (params.path) {
    params.path = params.path.replace(/\/api\/v[12]\//, `/${API_VERSION}/`);
  }
  const isAuthPath = AUTH_PUBLIC_PATHS.some(path => params.path.includes(path));
  const isRefreshPath = params.path.includes(`/${API_VERSION}/auth/refresh`);
  const isExcludedFromCache = CACHE_EXCLUDED_PATHS.some(p => params.path.includes(p));

  // Attempt to retrieve from cache for GET requests
  const cacheKey = getCacheKey(params);
  if (cacheKey && !isExcludedFromCache && cache.has(cacheKey)) {
    debugLog(`[clientService] Cache hit for: ${cacheKey}`);
    return Promise.resolve(cache.get(cacheKey)! as HttpResponse<T, E>);
  }

  // Ensure 'secure: true' is set for non-auth/non-refresh requests to trigger securityWorker,
  // unless explicitly set otherwise.
  if (!isAuthPath && !isRefreshPath && params.secure === undefined) {
    params.secure = true;
  }
  try {
    // @ts-ignore due to dynamic T, E types with specific HttpResponse
    const response = await originalRequest<T, E>(params);

    // Cache successful GET responses
    if (cacheKey && !isExcludedFromCache && response.ok) {
      debugLog(`[clientService] Caching response for: ${cacheKey}`);
      cache.set(cacheKey, response);
    }
    if ((params.method === 'POST' || params.method === 'PUT' || params.method === 'DELETE' || params.method === 'PATCH') && response.ok) {
      const resource = getResourceFromPath(params.path);
      if (resource) {
        debugLog(`[clientService] Invalidating cache for resource: ${resource}`);
        for (const key of cache.keys()) {
          if (key.startsWith(`resource:${resource}::`)) {
            cache.delete(key);
          }
        }
      } else {
        debugLog(`[clientService] No resource mapping found for path: ${params.path}. Clearing entire cache.`);
        cache.clear();
      }
    }
    return response;
  } catch (error: any) {
    const originalRequestParams = { ...params }; // Clone params for retry

    if (error && error.status === 401 && !isRefreshPath && !isAuthPath) {
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          // Use originalRequest directly for refresh to avoid recursive 401 handling
          // and to ensure it doesn't try to use the expired access token via securityWorker.
          const refreshToken = tokenStore.getItem(REFRESH_TOKEN_KEY);
          if (!refreshToken) {
            throw new Error("No refresh token available");
          }
          const refreshResponse = await originalRequest<Token, any>({
            path: `/${API_VERSION}/auth/refresh`,
            method: "POST",
            format: "json",
            credentials: 'include',
            secure: false,
            headers: {
              Authorization: `Bearer ${refreshToken}`
            }
          });

          saveAccessToken(refreshResponse.data.access_token);
          if (refreshResponse.data.refresh_token) {
            tokenStore.setItem(REFRESH_TOKEN_KEY, refreshResponse.data.refresh_token);
          }
          isRefreshing = false;
          onRefreshed(refreshResponse.data.access_token);
          notifyTokenRefreshListeners();
          // Retry original request
          // @ts-ignore
          const retriedResponse = await originalRequest<T, E>(originalRequestParams);
          // Cache successful GET responses after retry
          if (cacheKey && !isExcludedFromCache && retriedResponse.ok) { // Use original cacheKey
            debugLog(`[clientService] Caching response after refresh for: ${cacheKey}`);
            cache.set(cacheKey, retriedResponse);
          }
          return retriedResponse;
        } catch (refreshError: any) {
          isRefreshing = false;
          clearTokensFromStorage();
          onRefreshed(null); // Notify subscribers that refresh failed
          throw refreshError;
        }
      } else {
        return new Promise<HttpResponse<T, E>>((resolve, reject) => {
          refreshSubscribers.push((newToken: string | null) => {
            if (newToken) {
              originalRequest<T, E>(originalRequestParams).then(responseAfterRefresh => {
                // Cache successful GET responses after queued refresh
                if (cacheKey && !isExcludedFromCache && responseAfterRefresh.ok) { // Use original cacheKey
                  debugLog(`[clientService] Caching response after queued refresh for: ${cacheKey}`);
                  cache.set(cacheKey, responseAfterRefresh);
                }
                resolve(responseAfterRefresh);
              }).catch(reject);
            } else {
              reject(error); // Refresh failed, reject original error
            }
          });
        });
      }
    }
    throw error; // Re-throw other errors or if not 401
  }
};

/**
 * Save tokens after login. `rememberMe` picks the storage:
 * true → localStorage (stay logged in after the browser closes),
 * false → sessionStorage (logged out when the browser/tab closes).
 */
export const saveAuthTokens = (tokenResponse: Token, rememberMe = true) => {
  clearTokensFromStorage(); // drop any session left in the other storage
  tokenStore = rememberMe ? localStorage : sessionStorage;
  saveAccessToken(tokenResponse.access_token);
  if (tokenResponse.refresh_token) {
    tokenStore.setItem(REFRESH_TOKEN_KEY, tokenResponse.refresh_token);
  }
};

export const clearAuthTokens = () => {
  clearTokensFromStorage();
};

export const isAuthenticated = (): boolean => {
  return !!getAccessTokenFromStorage();
};

export default clientService;
